<template>
    <teleport to="body">
        <div v-if="show" class="modal show" role="modal">
            <div class="modal-backdrop show" style="z-index: unset" @mousedown="closeModal"></div>
            <div class="modal-dialog modal-xl popup">
                <div class="modal-content">
                    <div class="modal-header">
                        <div class="modal-title">
                            {{ currentTask?.title }}
                        </div>
                        <button type="button" class="close" @click="closeModal">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>

                    <div class="modal-body">
                        <div v-if="loading" class="text-center">
                            <i class="icofont-spinner"></i>
                        </div>
                        <div v-else>
                            <!-- Date Iteration Area -->
                            <div v-for="(dateData, dateString) in processedSelection" :key="dateString" class="mb-4">
                                <div class="card">
                                    <div class="card-header bg-light">
                                        <strong>{{ formatDateForDisplay(dateString) }}</strong>
                                    </div>
                                    <div class="card-body">
                                        <!-- Header Row for persons table -->
                                        <div class="row mb-2 font-weight-bold align-items-center g-2">
                                            <div class="col">{{ trans('Name') }}</div>
                                            <div class="col-auto"><div style="width: 175px;">{{ trans('Job') }}</div></div>
                                            <div class="col-auto"><div style="width: 75px;">{{ trans('Bill.') }}</div></div>
                                            <div class="col-auto"><div style="width: 100px;">{{ trans('Start') }}</div></div>
                                            <div class="col-auto"><div style="width: 100px;">{{ trans('End') }}</div></div>
                                            <div class="col-auto"><div style="width: 100px;">{{ trans('Total') }}</div></div>
                                            <div class="col-auto"><div style="width: 50px; text-align: right;">&nbsp;</div></div>
                                        </div>

                                        <!-- Person Iteration Area -->
                                        <div v-for="(personEntry, index) in dateData.persons" :key="personEntry.personId + '-' + index" class="row mb-2 align-items-center g-2">
                                            <div class="col">
                                                <!-- Display person name with clickable link icon -->
                                                <div class="person-name-display" :title="personEntry.personName">
                                                    <button 
                                                        v-if="shouldShowLinkButton(dateString, index)"
                                                        @click="togglePersonLink(dateString, index)"
                                                        class="btn btn-link link-toggle-btn p-0 mr-1"
                                                        :title="isLinkedPerson(dateString, index) ? 'Unlink from other persons' : 'Link with other persons'"
                                                    >
                                                        <i :class="getLinkIconClass(dateString, index)" class="link-icon"></i>
                                                    </button>
                                                    <span v-else-if="isLinkedPerson(dateString, index) && !isFirstInSlotGroup(dateString, index)" 
                                                          class="mr-1">
                                                        <i class="icofont-link link-icon" title="Linked to primary person"></i>
                                                    </span>
                                                    {{ personEntry.personName || 'N/A' }}
                                                </div>
                                            </div>
                                            <div class="col-auto">
                                                <div style="width: 175px;">
                                                    <v-select
                                                        v-model="personEntry.jobId"
                                                        :options="availableJobs"
                                                        label="title"
                                                        :reduce="job => job.id"
                                                        class="vue-select-style w-100"
                                                    ></v-select>
                                                </div>
                                            </div>
                                            <div class="col-auto">
                                                <div style="width: 70px; display: flex; justify-content: start; align-items: center; height: calc(1.5em + 0.5rem + 2px);">
                                                    <div class="switch-form">
                                                        <label class="switch">
                                                            <input type="checkbox" v-model="personEntry.billable">
                                                            <span class="slider round"></span>
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="col-auto">
                                                <input 
                                                    type="time" 
                                                    v-model="personEntry.startTime" 
                                                    @change="onTimeChange(dateString, index, 'start')" 
                                                    class="form-control form-control-sm" 
                                                    style="width: 100px;" 
                                                    :disabled="isLinkedPerson(dateString, index) && !isFirstInSlotGroup(dateString, index)"
                                                    :class="{ 'linked-field': isLinkedPerson(dateString, index) && !isFirstInSlotGroup(dateString, index) }"
                                                />
                                            </div>
                                            <div class="col-auto">
                                                <input 
                                                    type="time" 
                                                    v-model="personEntry.endTime" 
                                                    @change="onTimeChange(dateString, index, 'end')" 
                                                    class="form-control form-control-sm" 
                                                    style="width: 100px;" 
                                                    :disabled="isLinkedPerson(dateString, index) && !isFirstInSlotGroup(dateString, index)"
                                                    :class="{ 'linked-field': isLinkedPerson(dateString, index) && !isFirstInSlotGroup(dateString, index) }"
                                                />
                                            </div>
                                            <div class="col-auto">
                                                <input 
                                                    type="time" 
                                                    v-model="personEntry.totalHoursDisplay" 
                                                    @change="onTimeChange(dateString, index, 'total')" 
                                                    class="form-control form-control-sm" 
                                                    style="width: 100px;" 
                                                    :disabled="isLinkedPerson(dateString, index) && !isFirstInSlotGroup(dateString, index)"
                                                    :class="{ 'linked-field': isLinkedPerson(dateString, index) && !isFirstInSlotGroup(dateString, index) }"
                                                />
                                            </div>
                                            <div class="col-auto">
                                                <div style="width: 50px; display: flex; justify-content: center; align-items: center;">
                                                    <button @click="removePerson(dateString, index)" class="btn btn-danger btn-sm">
                                                        <i class="icofont-trash"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Equipment Iteration Area -->
                                        <div v-if="dateData.equipments && dateData.equipments.length > 0" class="mt-4">
                                            <div class="row mb-2 font-weight-bold align-items-center g-2">
                                                <div class="col">Equipment</div>
                                                <div class="col-auto"><div style="width: 100px;">Start</div></div>
                                                <div class="col-auto"><div style="width: 100px;">Einde</div></div>
                                                <div class="col-auto"><div style="width: 100px;">Totaal</div></div>
                                                <div class="col-auto"><div style="width: 50px; text-align: right;">&nbsp;</div></div>
                                            </div>

                                            <div v-for="(equipmentEntry, index) in dateData.equipments" :key="equipmentEntry.equipmentId + '-' + index" class="row mb-2 align-items-center g-2">
                                                <div class="col">
                                                    <div class="form-control form-control-sm" style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; background-color: #e9ecef;" :title="equipmentEntry.equipmentTitle">
                                                        <i class="icofont-vehicle-cement mr-2"></i>
                                                        {{ equipmentEntry.equipmentTitle || 'N/A' }}
                                                    </div>
                                                </div>
                                                <div class="col-auto">
                                                    <input type="time" v-model="equipmentEntry.startTime" @change="updateTotalHoursDisplay(equipmentEntry)" class="form-control form-control-sm" style="width: 100px;" />
                                                </div>
                                                <div class="col-auto">
                                                    <input type="time" v-model="equipmentEntry.endTime" @change="updateTotalHoursDisplay(equipmentEntry)" class="form-control form-control-sm" style="width: 100px;" />
                                                </div>
                                                <div class="col-auto">
                                                    <input type="time" v-model="equipmentEntry.totalHoursDisplay" @change="updateEndTimeFromTotal(equipmentEntry)" class="form-control form-control-sm" style="width: 100px;" />
                                                </div>
                                                <div class="col-auto">
                                                    <div style="width: 50px; display: flex; justify-content: center; align-items: center;">
                                                        <button @click="removeEquipment(dateString, index)" class="btn btn-danger btn-sm">
                                                            <i class="icofont-trash"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Add Person/Equipment Buttons -->
                                        <!-- <div class="row mt-2 g-2">
                                            <div class="col"></div>
                                            <div class="col-auto">
                                                <button @click="addAsset(dateString)" class="btn btn-primary btn-sm">
                                                    <i class="icofont-plus"></i> Add Asset
                                                </button>
                                            </div>
                                        </div> -->
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button @click="closeModal" class="btn btn-secondary">
                            {{ trans('cancel') }}
                        </button>
                        <button class="btn btn-primary" @click="savePlanning" :disabled="loading">
                            {{ trans('save') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </teleport>
</template>

<script>
import { emitter } from '../../plugins/Mitt'; // Assuming Mitt is set up
import { LocalDate, LocalTime, Duration, DateTimeFormatter } from '@js-joda/core';
import axios from 'axios'; // Added axios
import { mapState } from 'vuex';

// Mock initial data for dropdowns - will be replaced by API data
const MOCK_PERSONS_ARRAY = [
    { id: '1', name: 'Vincent Bibauw' },
    { id: '2', name: 'Jane Doe' },
    { id: '3', name: 'John Smith' },
];

const MOCK_PERSONS_MAP = MOCK_PERSONS_ARRAY.reduce((acc, person) => {
    acc[person.id] = person;
    return acc;
}, {});

const MOCK_JOBS_ARRAY = [
    { id: 'j1', name: 'Jobinhoud A' },
    { id: 'j2', name: 'Jobinhoud B' },
    { id: 'j3', name: 'Ontwikkeling' },
];

export default {
    name: 'PlanningIndividualPlanningComponent',
    // components: {}, // Add if any sub-components are created
    data() {
        return {
            show: false,
            loading: false,
            selectedSlotsInput: [],
            processedSelection: {},
            availableJobs: [],
            currentTask: null,
            apiPersonsData: {},
            apiEquipmentsData: {},
            apiDefaultWeekschedules: {},
            apiAbsenceTypes: {},
            deletedSlotIds: new Set(),
        };
    },
    computed: {
        ...mapState('planning', ['link_assets']),
    },
    methods: {
        openModal(selectionDetails) {
            this.selectedSlotsInput = selectionDetails;
            this.processedSelection = {};
            this.show = true;
            this.fetchInitialData();
        },
        closeModal() {
            this.show = false;
            this.loading = false;
            this.selectedSlotsInput = [];
            this.processedSelection = {};
            this.currentTask = null;
            this.availableJobs = [];
            this.apiPersonsData = {};
            this.apiEquipmentsData = {};
            this.apiDefaultWeekschedules = {};
            this.apiAbsenceTypes = {};
            this.deletedSlotIds.clear();
        },
        async fetchInitialData() {
            if (!this.selectedSlotsInput || this.selectedSlotsInput.length === 0) {
                this.loading = false;
                return;
            }
            this.loading = true;

            try {
                const { commonTaskId, uniquePersonIds, uniqueEquipmentIds, uniqueDates, initialGroupedByDate } = this.extractSelectionData();

                if (!commonTaskId || uniqueDates.length === 0) {
                    console.warn("Insufficient data to fetch individual planning details.");
                    this.loading = false;
                    return;
                }

                const response = await this.fetchApiData(commonTaskId, uniquePersonIds, uniqueEquipmentIds, uniqueDates);
                this.setApiData(response.data);
                
                this.processedSelection = this.buildProcessedSelection(initialGroupedByDate, response.data.slots);
            } catch (error) {
                console.error("Error fetching initial data for individual planning:", error);
            } finally {
                this.loading = false;
            }
        },

        extractSelectionData() {
            let commonTaskId = null;
            const personIdsSet = new Set();
            const equipmentIdsSet = new Set();
            const datesSet = new Set();
            const initialGroupedByDate = {};

            this.selectedSlotsInput.forEach(slot => {
                if (!commonTaskId && slot.taskId) commonTaskId = slot.taskId;
                
                if (slot.type === 'person') {
                    personIdsSet.add(slot.personId);
                } else if (slot.type === 'equipment') {
                    equipmentIdsSet.add(slot.equipmentId);
                }
                
                datesSet.add(slot.dateFormatted);

                if (!initialGroupedByDate[slot.dateFormatted]) {
                    initialGroupedByDate[slot.dateFormatted] = { persons: [], equipments: [] };
                }
                
                if (slot.type === 'person') {
                    initialGroupedByDate[slot.dateFormatted].persons.push({
                        personId: slot.personId,
                    });
                } else if (slot.type === 'equipment') {
                    initialGroupedByDate[slot.dateFormatted].equipments.push({
                        equipmentId: slot.equipmentId,
                    });
                }
            });

            return {
                commonTaskId,
                uniquePersonIds: Array.from(personIdsSet),
                uniqueEquipmentIds: Array.from(equipmentIdsSet),
                uniqueDates: Array.from(datesSet),
                initialGroupedByDate
            };
        },

        async fetchApiData(commonTaskId, uniquePersonIds, uniqueEquipmentIds, uniqueDates) {
            return await axios.get(route('planning.individual-planning.data'), {
                params: {
                    task_id: commonTaskId,
                    person_ids: uniquePersonIds,
                    equipment_ids: uniqueEquipmentIds,
                    dates: uniqueDates,
                    is_day_summary: this.selectedSlotsInput[0]?.isDaySummary ? 1 : 0
                }
            });
        },

        setApiData(data) {
            this.currentTask = data.task;
            this.availableJobs = data.jobs;
            this.apiPersonsData = data.persons;
            this.apiEquipmentsData = data.equipments;
            this.apiDefaultWeekschedules = data.default_weekschedules;
            this.apiAbsenceTypes = data.absence_types;
        },

        buildProcessedSelection(initialGroupedByDate, existingSlots) {
            const newProcessedSelection = {};

            if (this.selectedSlotsInput[0]?.isDaySummary) {
                return this.buildFromExistingSlots(existingSlots);
            } else {
                return this.buildFromNewSelection(initialGroupedByDate, existingSlots);
            }
        },

        buildFromExistingSlots(existingSlots) {
            const newProcessedSelection = {};

            for (const [dateString, slots] of Object.entries(existingSlots)) {
                if (!newProcessedSelection[dateString]) {
                    newProcessedSelection[dateString] = { persons: [], equipments: [] };
                }

                for (const slot of slots) {
                    this.processSlotPersons(slot, dateString, newProcessedSelection);
                    this.processSlotEquipments(slot, dateString, newProcessedSelection);
                }
            }

            return newProcessedSelection;
        },

        processSlotPersons(slot, dateString, processedSelection) {
            if (!slot.persons || slot.persons.length === 0) return;

            const isLinked = slot.persons.length > 1;

            for (const personSlot of slot.persons) {
                const personApiDetails = this.apiPersonsData[personSlot.person_id];
                if (!personApiDetails) continue;

                const personEntry = {
                    personId: personSlot.person_id,
                    personName: personApiDetails.name,
                    jobId: personSlot.job_id || personApiDetails.job_id,
                    billable: Boolean(personSlot.billable === 1),
                    startTime: slot.start_time ? slot.start_time.substring(0, 5) : '00:00',
                    totalHoursDisplay: this.formatDurationToTime(slot.duration),
                    endTime: '00:00',
                    absences: personApiDetails.absences ? personApiDetails.absences[dateString] : [],
                    scheduleInfo: this.apiDefaultWeekschedules[dateString] || null,
                    slotId: slot.id,
                    isLinked: isLinked
                };
                
                this.updateEndTimeFromTotal(personEntry);
                processedSelection[dateString].persons.push(personEntry);
            }
        },

        processSlotEquipments(slot, dateString, processedSelection) {
            if (!slot.equipments || slot.equipments.length === 0) return;

            for (const equipmentSlot of slot.equipments) {
                const equipmentApiDetails = this.apiEquipmentsData[equipmentSlot.equipment_id];
                if (!equipmentApiDetails) continue;

                const equipmentEntry = {
                    equipmentId: equipmentSlot.equipment_id,
                    equipmentTitle: equipmentApiDetails.title,
                    startTime: slot.start_time ? slot.start_time.substring(0, 5) : '00:00',
                    totalHoursDisplay: this.formatDurationToTime(slot.duration),
                    endTime: '00:00',
                    scheduleInfo: null,
                    slotId: slot.id
                };
                
                this.updateEndTimeFromTotal(equipmentEntry);
                processedSelection[dateString].equipments.push(equipmentEntry);
            }
        },

        buildFromNewSelection(initialGroupedByDate, existingSlots) {
            const newProcessedSelection = {};

            for (const dateString in initialGroupedByDate) {
                newProcessedSelection[dateString] = { persons: [], equipments: [] };

                this.processNewPersons(dateString, initialGroupedByDate, existingSlots, newProcessedSelection);
                this.processNewEquipments(dateString, initialGroupedByDate, existingSlots, newProcessedSelection);

                // Apply default linking for new selections
                this.applyDefaultLinking(dateString, newProcessedSelection);
            }

            return newProcessedSelection;
        },

        processNewPersons(dateString, initialGroupedByDate, existingSlots, processedSelection) {
            for (const tempPersonEntry of initialGroupedByDate[dateString].persons) {
                const personId = tempPersonEntry.personId;
                const personApiDetails = this.apiPersonsData[personId];
                if (!personApiDetails) continue;

                const existingSlot = existingSlots[dateString]?.persons?.find(person => 
                    person.person_id === parseInt(personId)
                );

                const personEntry = this.buildPersonEntry(personId, personApiDetails, dateString, existingSlot, existingSlots[dateString]);
                processedSelection[dateString].persons.push(personEntry);
            }
        },

        buildPersonEntry(personId, personApiDetails, dateString, existingSlot, existingSlotData) {
            let startTime = '00:00';
            let duration = '00:00';
            let slotId = null;
            let isLinked = false;

            if (existingSlot && existingSlotData) {
                startTime = existingSlotData.start_time ? existingSlotData.start_time.substring(0, 5) : '00:00';
                duration = this.formatDurationToTime(existingSlotData.duration);
                slotId = existingSlotData.id;
                isLinked = existingSlotData.persons?.length > 1;
            } else {
                const timing = this.getPersonTiming(personApiDetails, dateString);
                startTime = timing.startTime;
                duration = timing.duration;
            }

            const personEntry = {
                personId: personId,
                personName: personApiDetails.name,
                jobId: existingSlot?.job_id || personApiDetails.job_id,
                billable: existingSlot?.billable !== undefined ? Boolean(existingSlot.billable === 1) : Boolean(personApiDetails.billable === 1),
                startTime: startTime,
                totalHoursDisplay: duration,
                endTime: '00:00',
                absences: personApiDetails.absences ? personApiDetails.absences[dateString] : [],
                scheduleInfo: this.apiDefaultWeekschedules[dateString] || null,
                slotId: slotId,
                isLinked: isLinked
            };

            this.updateEndTimeFromTotal(personEntry);
            return personEntry;
        },

        getPersonTiming(personApiDetails, dateString) {
            const personSchedule = personApiDetails.weekschedules && personApiDetails.weekschedules[dateString] ? personApiDetails.weekschedules[dateString] : null;
            const defaultSchedule = this.apiDefaultWeekschedules && this.apiDefaultWeekschedules[dateString] ? this.apiDefaultWeekschedules[dateString] : null;

            if (personSchedule && personSchedule.time_start && personSchedule.time_duration) {
                return {
                    startTime: personSchedule.time_start.substring(0, 5),
                    duration: personSchedule.time_duration.substring(0, 5)
                };
            } else if (defaultSchedule && defaultSchedule.time_start && defaultSchedule.time_duration) {
                return {
                    startTime: defaultSchedule.time_start.substring(0, 5),
                    duration: defaultSchedule.time_duration.substring(0, 5)
                };
            }

            return { startTime: '00:00', duration: '00:00' };
        },

        processNewEquipments(dateString, initialGroupedByDate, existingSlots, processedSelection) {
            for (const tempEquipmentEntry of initialGroupedByDate[dateString].equipments) {
                const equipmentId = tempEquipmentEntry.equipmentId;
                const equipmentApiDetails = this.apiEquipmentsData[equipmentId];
                if (!equipmentApiDetails) continue;

                const existingSlot = existingSlots[dateString]?.equipments?.find(equipment => 
                    equipment.equipment_id === parseInt(equipmentId)
                );

                const equipmentEntry = this.buildEquipmentEntry(equipmentId, equipmentApiDetails, dateString, existingSlot, existingSlots[dateString]);
                processedSelection[dateString].equipments.push(equipmentEntry);
            }
        },

        buildEquipmentEntry(equipmentId, equipmentApiDetails, dateString, existingSlot, existingSlotData) {
            let startTime = '00:00';
            let duration = '00:00';
            let slotId = null;

            if (existingSlot && existingSlotData) {
                startTime = existingSlotData.start_time ? existingSlotData.start_time.substring(0, 5) : '00:00';
                duration = this.formatDurationToTime(existingSlotData.duration);
                slotId = existingSlotData.id;
            } else {
                const defaultSchedule = this.apiDefaultWeekschedules && this.apiDefaultWeekschedules[dateString] ? this.apiDefaultWeekschedules[dateString] : null;
                if (defaultSchedule && defaultSchedule.time_start && defaultSchedule.time_duration) {
                    startTime = defaultSchedule.time_start.substring(0, 5);
                    duration = defaultSchedule.time_duration.substring(0, 5);
                }
            }

            const equipmentEntry = {
                equipmentId: equipmentId,
                equipmentTitle: equipmentApiDetails.title,
                startTime: startTime,
                totalHoursDisplay: duration,
                endTime: '00:00',
                scheduleInfo: this.apiDefaultWeekschedules[dateString] || null,
                slotId: slotId
            };

            this.updateEndTimeFromTotal(equipmentEntry);
            return equipmentEntry;
        },

        applyDefaultLinking(dateString, processedSelection) {
            const persons = processedSelection[dateString].persons;
            if (persons.length > 1 && this.link_assets) {
                const allPersonsNew = persons.every(p => !p.slotId);
                if (allPersonsNew) {
                    const tempSlotId = this.generateTempSlotId();
                    persons.forEach(person => {
                        person.slotId = tempSlotId;
                        person.isLinked = true;
                    });
                }
            }
        },

        generateTempSlotId() {
            return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        },

        formatDateForDisplay(dateString) {
            if (!dateString) {
                return 'Invalid date';
            }
            const date = LocalDate.parse(dateString);
            return date.format(DateTimeFormatter.ofPattern('dd/MM/yyyy'))
        },
        formatDurationToTime(duration) {
            const hours = Math.floor(duration / 60);
            const minutes = duration % 60;
            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        },
        updateTotalHoursDisplay(personEntry) {
            if (!personEntry.startTime || !personEntry.endTime) {
                personEntry.totalHoursDisplay = '00:00';
                return;
            }
            try {
                const start = LocalTime.parse(personEntry.startTime);
                const end = LocalTime.parse(personEntry.endTime);

                if (end.isBefore(start)) { // Or end.equals(start)
                    personEntry.totalHoursDisplay = '00:00';
                    return;
                }

                const duration = Duration.between(start, end);
                const hours = duration.toHours();
                const minutes = duration.toMinutes() % 60;
                
                personEntry.totalHoursDisplay = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
            } catch (e) {
                console.error("Error calculating total hours:", e);
                personEntry.totalHoursDisplay = '00:00'; // Fallback
            }
        },
        updateEndTimeFromTotal(personEntry) {
            if (!personEntry.startTime || !personEntry.totalHoursDisplay) {
                // Cannot calculate if start time or total hours is missing
                return;
            }
            try {
                const start = LocalTime.parse(personEntry.startTime);
                const parts = personEntry.totalHoursDisplay.split(':');
                if (parts.length !== 2) {
                    // Invalid total hours format, optionally reset or warn
                    this.updateTotalHoursDisplay(personEntry); // Recalculate total from start/end as a fallback
                    return;
                }
                const hours = parseInt(parts[0], 10);
                const minutes = parseInt(parts[1], 10);

                if (isNaN(hours) || isNaN(minutes) || hours < 0 || minutes < 0 || minutes >= 60) {
                    // Invalid numbers in total hours, optionally reset or warn
                    this.updateTotalHoursDisplay(personEntry); // Recalculate total from start/end as a fallback
                    return;
                }

                const durationToAdd = Duration.ofHours(hours).plusMinutes(minutes);
                const newEnd = start.plus(durationToAdd);
                personEntry.endTime = newEnd.format(DateTimeFormatter.ISO_LOCAL_TIME).substring(0,5); // Format as HH:mm

                // Ensure totalHoursDisplay is well-formatted after successful calculation
                personEntry.totalHoursDisplay = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

            } catch (e) {
                console.error("Error updating end time from total:", e);
                // If an error occurs (e.g. parsing startTime), recalculate totalHoursDisplay from existing start/end
                // This prevents inconsistent states if totalHoursDisplay was valid but startTime became invalid somehow before this call
                this.updateTotalHoursDisplay(personEntry);
            }
        },
        addAsset(dateString) {
            console.log("TODO: Open asset search modal for date:", dateString);
            // emitter.emit('open-asset-search-modal', { dateString: dateString });
        },
        handleAssetSelectedFromSearch({ asset, dateString }) {
            if (!this.processedSelection[dateString] || !asset || !asset.id) {
                console.warn("Could not add asset from search. Invalid data received or date not found.", {asset, dateString});
                return;
            }

            if (asset.type === 'person') {
                const newPersonEntry = {
                    personId: asset.id,
                    personName: asset.name,
                    jobId: this.availableJobs.length > 0 ? this.availableJobs[0].id : null,
                    billable: true,
                    startTime: '00:00',
                    endTime: '00:00',
                    totalHoursDisplay: '00:00',
                    isNew: true,
                    linkedState: 'new',
                    originallyLinked: false
                };
                this.updateTotalHoursDisplay(newPersonEntry);
                this.processedSelection[dateString].persons.push(newPersonEntry);
            } else if (asset.type === 'equipment') {
                const newEquipmentEntry = {
                    equipmentId: asset.id,
                    equipmentTitle: asset.title,
                    startTime: '00:00',
                    endTime: '00:00',
                    totalHoursDisplay: '00:00',
                    isNew: true
                };
                this.updateTotalHoursDisplay(newEquipmentEntry);
                this.processedSelection[dateString].equipments.push(newEquipmentEntry);
            }
        },
        removePerson(dateString, personIndex) {
            if (this.processedSelection[dateString] && this.processedSelection[dateString].persons[personIndex]) {
                const person = this.processedSelection[dateString].persons[personIndex];
                if (person.slotId && !person.slotId.toString().startsWith('temp_')) {
                    this.deletedSlotIds.add(person.slotId);
                }
                this.processedSelection[dateString].persons.splice(personIndex, 1);
            }
        },
        removeEquipment(dateString, equipmentIndex) {
            if (this.processedSelection[dateString] && this.processedSelection[dateString].equipments[equipmentIndex]) {
                const equipment = this.processedSelection[dateString].equipments[equipmentIndex];
                if (equipment.slotId) {
                    this.deletedSlotIds.add(equipment.slotId);
                }
                this.processedSelection[dateString].equipments.splice(equipmentIndex, 1);
            }
        },
        savePlanning() {
            this.loading = true;

            const planningData = this.preparePlanningData();

            axios.post(route('planning.individual-planning.post'), {
                task_id: this.currentTask.id,
                planning_data: planningData,
                deleted_slot_ids: Array.from(this.deletedSlotIds)
            })
            .then(response => {
                emitter.emit('planning-slots-changed', response.data);
                this.closeModal();
            })
            .catch(error => {
                console.error('Error saving planning:', error);
            })
            .finally(() => {
                this.loading = false;
            });
        },

        preparePlanningData() {
            const planningData = {};
            
            for (const [dateString, dateData] of Object.entries(this.processedSelection)) {
                planningData[dateString] = {
                    persons: this.preparePersonsData(dateString, dateData.persons),
                    equipments: this.prepareEquipmentsData(dateData.equipments)
                };
            }
            
            return planningData;
        },

        preparePersonsData(dateString, persons) {
            return persons.map(person => {
                const linkedState = this.determineLinkedState(person, persons);
                const slotId = this.prepareSlotId(person, linkedState);
                
                return {
                    slot_id: slotId,
                    person_id: person.personId,
                    job_id: person.jobId,
                    billable: person.billable ? 1 : 0,
                    start_time: person.startTime,
                    end_time: person.endTime,
                    linked_state: linkedState
                };
            });
        },

        prepareEquipmentsData(equipments) {
            return equipments.map(equipment => ({
                slot_id: equipment.slotId,
                equipment_id: equipment.equipmentId,
                start_time: equipment.startTime,
                end_time: equipment.endTime
            }));
        },

        determineLinkedState(person, allPersons) {
            if (!person.slotId) return 'individual';
            
            const personsWithSameSlot = allPersons.filter(p => p.slotId === person.slotId);
            return personsWithSameSlot.length > 1 ? 'linked' : 'individual';
        },

        prepareSlotId(person, linkedState) {
            // For temporary slot IDs, always return null to create new slots
            if (this.isTempSlotId(person.slotId)) {
                return null;
            }
            
            // For existing slots, keep the slot_id to update the existing slot
            // Only add to deletedSlotIds if the person was actually removed
            return person.slotId;
        },

        isTempSlotId(slotId) {
            return slotId && slotId.toString().startsWith('temp_');
        },
        
        togglePersonLink(dateString, personIndex) {
            const currentlyLinked = this.isLinkedPerson(dateString, personIndex);
            
            if (currentlyLinked) {
                this.unlinkPerson(dateString, personIndex);
            } else {
                this.linkPerson(dateString, personIndex);
            }
        },

        linkPerson(dateString, personIndex) {
            const persons = this.processedSelection[dateString].persons;
            const targetPerson = persons[personIndex];
            
            // Find existing linked group to join
            const existingLinkedSlotId = this.findExistingLinkedGroup(persons, targetPerson);
            
            if (existingLinkedSlotId) {
                this.joinExistingGroup(targetPerson, persons, existingLinkedSlotId);
            } else {
                this.createNewLinkedGroup(targetPerson, persons, personIndex);
            }
        },

        findExistingLinkedGroup(persons, targetPerson) {
            for (const person of persons) {
                if (person !== targetPerson && person.slotId) {
                    const personsWithSameSlot = persons.filter(p => p.slotId === person.slotId);
                    if (personsWithSameSlot.length > 1) {
                        return person.slotId;
                    }
                }
            }
            return null;
        },

        joinExistingGroup(targetPerson, persons, existingLinkedSlotId) {
            const primaryPerson = persons.find(p => p.slotId === existingLinkedSlotId);
            targetPerson.slotId = existingLinkedSlotId;
            targetPerson.startTime = primaryPerson.startTime;
            targetPerson.endTime = primaryPerson.endTime;
            targetPerson.totalHoursDisplay = primaryPerson.totalHoursDisplay;
            targetPerson.isLinked = true;
        },

        createNewLinkedGroup(targetPerson, persons, personIndex) {
            let primaryPerson = persons[0];
            if (persons.length > 1 && personIndex === 0) {
                primaryPerson = persons[1];
            }
            
            const linkSlotId = primaryPerson.slotId || this.generateTempSlotId();
            
            targetPerson.slotId = linkSlotId;
            primaryPerson.slotId = linkSlotId;
            
            targetPerson.startTime = primaryPerson.startTime;
            targetPerson.endTime = primaryPerson.endTime;
            targetPerson.totalHoursDisplay = primaryPerson.totalHoursDisplay;
            
            targetPerson.isLinked = true;
            primaryPerson.isLinked = true;
        },

        unlinkPerson(dateString, personIndex) {
            const person = this.processedSelection[dateString].persons[personIndex];
            
            if (person.slotId && !this.isTempSlotId(person.slotId)) {
                person.slotId = null;
            } else {
                person.slotId = this.generateTempSlotId();
            }
            
            person.isLinked = false;
        },

        isLinkedPerson(dateString, personIndex) {
            const person = this.processedSelection[dateString]?.persons[personIndex];
            if (!person || !person.slotId) return false;
            
            const personsWithSameSlot = this.processedSelection[dateString].persons.filter(p => p.slotId === person.slotId);
            return personsWithSameSlot.length > 1;
        },

        isFirstInSlotGroup(dateString, personIndex) {
            const person = this.processedSelection[dateString]?.persons[personIndex];
            if (!person || !person.slotId) return true;
            
            const firstIndex = this.processedSelection[dateString].persons.findIndex(p => p.slotId === person.slotId);
            return firstIndex === personIndex;
        },

        getLinkedPersons(dateString, slotId) {
            if (!slotId) return [];
            return this.processedSelection[dateString].persons.filter(p => p.slotId === slotId);
        },

        onTimeChange(dateString, personIndex, changeType) {
            const person = this.processedSelection[dateString].persons[personIndex];
            
            if (changeType === 'start' || changeType === 'end') {
                this.updateTotalHoursDisplay(person);
            } else if (changeType === 'total') {
                this.updateEndTimeFromTotal(person);
            }

            if (this.isLinkedPerson(dateString, personIndex) && this.isFirstInSlotGroup(dateString, personIndex)) {
                this.updateLinkedPersons(dateString, person.slotId, person);
            }
        },

        updateLinkedPersons(dateString, slotId, primaryPerson) {
            const linkedPersons = this.getLinkedPersons(dateString, slotId);
            
            linkedPersons.forEach(linkedPerson => {
                if (linkedPerson === primaryPerson) return;
                
                linkedPerson.startTime = primaryPerson.startTime;
                linkedPerson.endTime = primaryPerson.endTime;
                linkedPerson.totalHoursDisplay = primaryPerson.totalHoursDisplay;
            });
        },

        getLinkIconClass(dateString, personIndex) {
            const person = this.processedSelection[dateString]?.persons[personIndex];
            if (!person || !person.slotId) return 'icofont-link-broken';
            
            const personsWithSameSlot = this.processedSelection[dateString].persons.filter(p => p.slotId === person.slotId);
            const isLinked = personsWithSameSlot.length > 1;
            
            return isLinked ? 'icofont-link' : 'icofont-link-broken';
        },

        shouldShowLinkButton(dateString, personIndex) {
            const dateData = this.processedSelection[dateString];
            if (!dateData || dateData.persons.length <= 1) return false;
            
            const person = dateData.persons[personIndex];
            if (!person) return false;
            
            // For existing slots, only show link button if they were originally linked
            if (person.slotId && !this.isTempSlotId(person.slotId)) {
                return person.isLinked;
            }
            
            // For new selections, respect the link_assets setting
            return this.link_assets;
        },
    },
    mounted() {
        // Assuming 'open-individual-planning-form' is the event emitted by ProjectPersonsComponent
        // with payload: [{ taskId: '...', personId: '...', dateFormatted: '...' }, ...]
        emitter.on('open-individual-planning-form', this.openModal);
        // TODO: Add listener for asset selected from search modal
        // emitter.on('asset-selected-for-individual-planning', this.handleAssetSelectedFromSearch);
    },
    beforeUnmount() {
        emitter.off('open-individual-planning-form', this.openModal);
        // TODO: Remove listener for asset selected from search modal
        // emitter.off('asset-selected-for-individual-planning', this.handleAssetSelectedFromSearch);
    }
};
</script>

<style scoped lang="scss">
.popup {
    /* Custom styles for this specific popup if needed */
    /* For example, if modal-xl is not enough */
}
.modal-body {
    max-height: 70vh; /* Or adjust as needed */
    overflow-y: auto;
}

/* Style adjustments for v-select to better match form-control-sm if needed */
/* You might need to target vue-select internal classes more specifically */
.vue-select-style .vs__dropdown-toggle {
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
    min-height: calc(1.5em + 0.5rem + 2px); /* Matches form-control-sm height */
    font-size: 0.875rem; /* Matches form-control-sm font-size */
}

.vue-select-style .vs__selected {
    font-size: 0.875rem; /* Matches form-control-sm font-size */
    margin: 2px 0 0; /* Adjust vertical alignment if needed */
}

.linked-field {
    background-color: #e9ecef !important;
    border-color: #ced4da;
}

.linked-field:disabled {
    background-color: #e9ecef !important;
    opacity: 0.8;
}

/* Link icon styling */
.link-primary {
    color: #007bff;
    font-size: 1em;
}

.link-secondary {
    color: #6c757d;
    font-size: 0.9em;
}

.link-icon {
    color: #007bff;
    font-size: 0.9em;
}

.link-toggle-btn {
    border: none !important;
    box-shadow: none !important;
    color: #6c757d;
    transition: color 0.2s ease;
}

.link-toggle-btn:hover {
    color: #007bff !important;
    text-decoration: none;
}

.link-toggle-btn:focus {
    box-shadow: none !important;
}

.link-toggle-btn .link-icon {
    color: inherit;
    transition: transform 0.2s ease;
}

.link-toggle-btn:hover .link-icon {
    transform: scale(1.1);
}

/* Show different icon on hover to indicate action */
.link-toggle-btn:hover .icofont-link::before {
    content: "\edf8"; /* link-broken icon - show unlink action */
}

.link-toggle-btn:hover .icofont-link-broken::before {
    content: "\ef71"; /* link icon - show link action */
}

.person-name-display {
    padding: 0.375rem 0;
    font-size: 0.875rem;
    line-height: 1.5;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    background-color: transparent;
    border: none;
    border-bottom: 1px solid #dee2e6;
    border-radius: 0;
    min-height: calc(1.5em + 0.5rem + 2px);
    display: flex;
    align-items: center;
}

/* Responsive table headers were removed as per request for desktop-only focus */
/*
.d-md-none {
    @media (min-width: 768px) {
        display: none !important;
    }
}
.mb-md-0 {
    @media (min-width: 768px) {
        margin-bottom: 0 !important;
    }
}
*/
</style>
